import {
    Alert,
    Badge,
    Box,
    Heading,
    Stack,
    Text,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeFontVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import ActivityBadges from "components/selection-components/select-activity/activity-badges/activity-badges";
import ActivityTimeSlot from "components/selection-components/select-activity/activity-time-slot";
import TimeSetAccordion from "components/selection-components/select-activity/time-set-accordion";
import { useBookedActivities } from "components/selection-components/select-activity/use-reserved-activities";
import { useTimesToNudge } from "components/selection-components/select-activity/use-times-to-nudge";
import WaitListTimeSetTitle from "components/selection-components/select-time/time-set/wait-list-time-set-title";
import ShowMoreText from "components/show-more-text/show-more-text";
import TimeSlotGrid from "components/time-slot-grid";
import WaitListTimeSlot from "components/wait-list/wait-list-time-slot";
import { useTimeSetsFilters } from "hooks/use-time-slot-filters";
import { useTranslation } from "react-i18next";
import { AvailableTimesSection } from "types/available-times";
import testId from "types/test-id";
import { TimeSlot } from "types/time-slot";
import { mergeTimeSetsByTitle } from "utils/time-rule-utils";

interface IActivityModal {
    section: AvailableTimesSection;
    selectedTime: TimeSlot | undefined;
    setSelectedTime: (timeSlot: TimeSlot) => void;
    setSelectedWaitListTimes: (time: TimeSlot) => void;
    selectedWaitListTimes: TimeSlot[];
}

const ActivityModalBody = ({
    section,
    selectedTime,
    setSelectedTime,
    setSelectedWaitListTimes,
    selectedWaitListTimes,
}: IActivityModal) => {
    const { t } = useTranslation();
    const bookedActivities = useBookedActivities(section.id);
    const { data: reservation } = useWebBooking();
    const openTimeSets = section.timeSets.filter((x) => !x.isTextRow && !x.isClosed);
    const { bookableTimeSets, waitListTimeSets } = useTimeSetsFilters(openTimeSets);

    const mergedTimeSets = mergeTimeSetsByTitle(bookableTimeSets);
    const mergedWaitListTimeSets = mergeTimeSetsByTitle(waitListTimeSets);
    const useAccordion = mergedTimeSets.length > 1;
    const timesToNudgeMap = useTimesToNudge(section);

    const hasTimeSlots = mergedTimeSets.some((timeSet) => timeSet.times.length > 0);
    const hasWaitListTimeSlots = mergedWaitListTimeSets.some((timeSet) => timeSet.times.length > 0);

    const hasPreviousActivities = (reservation?.getBookTimes()?.length ?? 0) > 0;
    const singelGrid = () =>
        mergedTimeSets.map((timeSet) => (
            <TimeSlotGrid
                key={`timeSet${timeSet.id}`}
                items={timeSet.times.map((time) => (
                    <ActivityTimeSlot
                        key={time.start.format("HH:mm")}
                        time={time}
                        selectedTime={selectedTime}
                        onClick={() => setSelectedTime(time)}
                        timesToNudge={timesToNudgeMap.get(timeSet.id) ?? []}
                    />
                ))}
            />
        ));

    return (
        <Stack spacing={ThemeSpaceVariable.None}>
            <Stack
                spacing={ThemeSpaceVariable.None}
                p={ThemeSpaceVariable.Medium}
                pb={ThemeSpaceVariable.Small}
            >
                <Heading
                    as="h2"
                    fontWeight="medium"
                    fontSize={ThemeFontSizeVariable.XLarge}
                    fontFamily={ThemeFontVariable.SubHeader}
                    mb={ThemeSpaceVariable.Small}
                >
                    {section.name}
                </Heading>
                <ShowMoreText
                    text={section.description}
                    initialLines={hasTimeSlots || hasWaitListTimeSlots ? 2 : undefined}
                    textProps={{ fontWeight: "400" }}
                />
                <ActivityBadges section={section} inModal />
            </Stack>

            <Stack p={ThemeSpaceVariable.Medium} spacing={ThemeSpaceVariable.None}>
                {hasTimeSlots && hasPreviousActivities && (
                    <Box pb={ThemeSpaceVariable.Medium}>
                        <Stack spacing={ThemeSpaceVariable.Small}>
                            <Text>{t("activityModal.activites")}</Text>
                            {bookedActivities.map((activityBadge, idx) => (
                                <Badge.Primary key={`${activityBadge}-${idx}`}>
                                    {activityBadge}
                                </Badge.Primary>
                            ))}
                        </Stack>
                    </Box>
                )}
                {hasTimeSlots && (
                    <>
                        <Heading
                            as="h3"
                            fontSize={ThemeFontSizeVariable.Large}
                            fontFamily={ThemeFontVariable.SubHeader}
                            fontWeight="medium"
                            pb={
                                mergedTimeSets[0]?.comment
                                    ? ThemeSpaceVariable.None
                                    : ThemeSpaceVariable.Medium
                            }
                        >
                            {t("selectTime")}
                        </Heading>

                        {useAccordion ? (
                            <TimeSetAccordion
                                timeSets={mergedTimeSets}
                                selectedTime={selectedTime ?? new TimeSlot()}
                                onClick={setSelectedTime}
                                timesToNudgeMap={timesToNudgeMap}
                            />
                        ) : (
                            <>
                                {mergedTimeSets[0]?.comment && (
                                    <Text
                                        pt={ThemeSpaceVariable.XSmall}
                                        pb={ThemeSpaceVariable.Medium}
                                        color={ThemeColorVariable.OnSurfaceSubdued}
                                        data-testid={testId.timeSet.comment}
                                    >
                                        {mergedTimeSets[0]?.comment}
                                    </Text>
                                )}
                                {hasTimeSlots && singelGrid()}
                            </>
                        )}
                    </>
                )}
                {mergedWaitListTimeSets.length > 0 && (
                    <Stack spacing={ThemeSpaceVariable.None}>
                        <WaitListTimeSetTitle
                            mt={hasTimeSlots ? ThemeSpaceVariable.Large : ThemeSpaceVariable.None}
                            mb={ThemeSpaceVariable.Small}
                        />
                        <Alert type="info" mb={ThemeSpaceVariable.Medium}>
                            {t("waitList.cantCombineWaitListWithBookedTimes")}
                        </Alert>
                        {mergedWaitListTimeSets.map((timeSet) => (
                            <TimeSlotGrid
                                key={`timeSet${timeSet.id}`}
                                items={timeSet.times.map((time) => (
                                    <WaitListTimeSlot
                                        key={time.start.format("HH:mm")}
                                        time={time}
                                        selectedTimes={selectedWaitListTimes}
                                        onTimeClick={setSelectedWaitListTimes}
                                    />
                                ))}
                            />
                        ))}
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};

export default ActivityModalBody;
